/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as SPALink, NavLink as NavSPALink} from 'react-router-dom'
import useMultiSite from '../../hooks/use-multi-site'

/**
 * Checks if a given URL string contains a protocol (http or https).
 */
const hasProtocol = (url) => {
    return /^https?:\/\//i.test(url)
}

/**
 * A React component that renders a link, which can be either an internal or external link.
 * This component uses Chakra UI's `ChakraLink` and supports both single-page application (SPA)
 * navigation and traditional anchor links.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.Ref} ref - A ref that is forwarded to the underlying `ChakraLink` component.
 *
 * @returns {JSX.Element} A `ChakraLink` component configured as either an internal or external link.
 */
const Link = React.forwardRef(({href, to, useNavLink = false, ...props}, ref) => {
    const _href = to || href
    const {buildUrl} = useMultiSite()
    const isExternal = hasProtocol(_href)

    // use buildUrl only for internal links
    const finalUrl = isExternal ? _href : buildUrl(_href)

    const linkProps = isExternal
        ? {as: 'a', href: finalUrl, isExternal: true}
        : {as: useNavLink ? NavSPALink : SPALink, to: finalUrl, ...(useNavLink && {exact: true})}

    return <ChakraLink {...linkProps} {...props} ref={ref} />
})

Link.displayName = 'Link'

Link.propTypes = {href: PropTypes.string, to: PropTypes.string, useNavLink: PropTypes.bool}

export default React.memo(Link)
